import { useEffect, useRef, useState } from "react";

const DEFAULT_DELAY_MS = 300;

/** The longest search text the list endpoints accept (`q` max_length in backend/modules/listing.py); a longer one is a 422. */
export const SEARCH_MAX_LENGTH = 100;

/** The longest free-text filter the list endpoints accept (`FILTER_MAX_LENGTH` in backend/modules/listing.py); a longer one is a 422. */
export const FILTER_MAX_LENGTH = 255;

/**
 * Every Unicode control character (category Cc: U+0000-U+001F and U+007F-U+009F, tab and newline
 * included). The list endpoints refuse them with a 422 (`_NO_CONTROL_CHARS` in
 * backend/modules/listing.py), and Mongo `$regex` refuses an embedded NUL. Kept a plain literal so a
 * backend test can read it and pin it to the backend rule. Global: use it with `replace`, not
 * `test` (a global regex's `test` resumes from `lastIndex`).
 */
// Matching control characters is this regex's whole purpose.
// eslint-disable-next-line no-control-regex
export const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/g;

/**
 * The search text as it is sent: each control character becomes a space (a pasted tab or line
 * break separates words, it does not glue them), then the ends are trimmed. A refused character
 * is never sent, because the 422 it causes cannot be fixed by retrying.
 */
export function normalizeSearch(text) {
  return text.replace(CONTROL_CHARACTERS, " ").trim();
}

/**
 * Free-text search for a paged server list.
 *
 * `search` is what the input shows; `q` is what the server is asked, and it only changes
 * once typing pauses. `onCommit` runs in the SAME React update as `q`, which is what lets
 * the caller reset its page together with the new query: resetting it in a separate effect
 * would first fire one request for the new query on the old page.
 *
 * `q` is always `normalizeSearch(search)`. A commit that would not change `q` (typing then
 * deleting back to the same text, trailing whitespace, a control character where a space was)
 * does nothing, so it never resets a page the operator turned to.
 *
 * The fourth element, `commitNow`, commits the text on screen without waiting for the pause
 * (the operator pressed Enter), under the same rules.
 */
export function useDebouncedSearch(onCommit, delayMs = DEFAULT_DELAY_MS) {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const committed = useRef("");
  const commitCallback = useRef(onCommit);
  commitCallback.current = onCommit;

  const commit = (text) => {
    const next = normalizeSearch(text);
    if (next === committed.current) return;
    committed.current = next;
    setQ(next);
    commitCallback.current?.();
  };

  useEffect(() => {
    if (normalizeSearch(search) === committed.current) return undefined;
    const timer = setTimeout(() => commit(search), delayMs);
    return () => clearTimeout(timer);
    // `commit` only touches refs and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, delayMs]);

  return [search, setSearch, q, () => commit(search)];
}
